/**
 * Handles file upload preview and validation for design upload forms
 */

class DesignUploadForm {
  constructor(formElement) {
    this.form = formElement;
    this.fileInput = this.form.querySelector('input[type="file"]');
    this.previewContainer = this.form.querySelector('.design-upload-form__file-preview');
    
    if (!this.fileInput || !this.previewContainer) return;
    
    this.maxFiles = parseInt(this.fileInput.dataset.maxFiles || '10', 10);
    this.maxSize = parseInt(this.fileInput.dataset.maxSize || '10485760', 10); // 10MB default
    this.selectedFiles = [];
    
    this.init();
  }
  
  init() {
    this.fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    
    // Prevent form submission if validation fails and sync files
    this.form.addEventListener('submit', (e) => {
      // Sync files to input before submission
      this.updateFileInput();
      
      if (!this.validateFiles()) {
        e.preventDefault();
        return false;
      }
    });
  }
  
  handleFileSelect(event) {
    const files = Array.from(event.target.files);
    
    // Check max files limit
    if (this.selectedFiles.length + files.length > this.maxFiles) {
      this.showError(`Maximum ${this.maxFiles} files allowed. Please remove some files first.`);
      event.target.value = '';
      return;
    }
    
    // Validate and add files
    const validFiles = [];
    for (const file of files) {
      if (this.validateFile(file)) {
        validFiles.push(file);
        this.selectedFiles.push(file);
      }
    }
    
    // Update preview
    this.updatePreview();
    
    // Sync files back to input (needed because we maintain files in selectedFiles array)
    this.updateFileInput();
  }
  
  validateFile(file) {
    // Check file size
    if (file.size > this.maxSize) {
      this.showError(`File "${file.name}" exceeds maximum size of ${this.formatFileSize(this.maxSize)}.`);
      return false;
    }
    
    // Check file type (basic check - browser handles accept attribute)
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const validPdfTypes = ['application/pdf'];
    
    if (!validImageTypes.includes(file.type) && !validPdfTypes.includes(file.type)) {
      this.showError(`File "${file.name}" is not a supported format. Please upload images or PDF files.`);
      return false;
    }
    
    return true;
  }
  
  validateFiles() {
    // Check if required and no files selected
    if (this.fileInput.hasAttribute('required') && this.selectedFiles.length === 0) {
      this.showError('Please upload at least one file.');
      return false;
    }
    
    return true;
  }
  
  updatePreview() {
    this.previewContainer.innerHTML = '';
    
    this.selectedFiles.forEach((file, index) => {
      const previewItem = document.createElement('div');
      previewItem.className = 'design-upload-form__file-preview-item';
      previewItem.dataset.fileIndex = index;
      
      if (file.type.startsWith('image/')) {
        const img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        img.alt = file.name;
        previewItem.appendChild(img);
      } else {
        // For PDF files, show a placeholder
        const pdfPlaceholder = document.createElement('div');
        pdfPlaceholder.style.cssText = 'width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f0f0f0; color: #666; font-size: 12px;';
        pdfPlaceholder.textContent = 'PDF';
        previewItem.appendChild(pdfPlaceholder);
      }
      
      // File name
      const fileName = document.createElement('div');
      fileName.className = 'design-upload-form__file-preview-item-name';
      fileName.textContent = file.name;
      previewItem.appendChild(fileName);
      
      // Remove button
      const removeBtn = document.createElement('button');
      removeBtn.className = 'design-upload-form__file-preview-item-remove';
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', `Remove ${file.name}`);
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', () => this.removeFile(index));
      previewItem.appendChild(removeBtn);
      
      this.previewContainer.appendChild(previewItem);
    });
  }
  
  removeFile(index) {
    this.selectedFiles.splice(index, 1);
    this.updatePreview();
    this.updateFileInput();
  }
  
  updateFileInput() {
    // Create a new DataTransfer object to update the file input
    const dataTransfer = new DataTransfer();
    this.selectedFiles.forEach(file => dataTransfer.items.add(file));
    this.fileInput.files = dataTransfer.files;
  }
  
  showError(message) {
    // Remove existing error messages
    const existingError = this.form.querySelector('.design-upload-form__file-error');
    if (existingError) {
      existingError.remove();
    }
    
    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'design-upload-form__file-error';
    errorDiv.style.cssText = 'color: var(--color-error, #d32f2f); margin-top: var(--gap-sm); font-size: 0.875rem;';
    errorDiv.textContent = message;
    
    this.fileInput.parentElement.appendChild(errorDiv);
    
    // Remove error after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }
  
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

// Initialize for all design upload forms on the page
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('.design-upload-form__form');
  forms.forEach(form => {
    new DesignUploadForm(form);
  });
});

// Also handle dynamically added forms (e.g., via AJAX)
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          const forms = node.querySelectorAll ? node.querySelectorAll('.design-upload-form__form') : [];
          forms.forEach(form => {
            if (!form.dataset.uploadInitialized) {
              form.dataset.uploadInitialized = 'true';
              new DesignUploadForm(form);
            }
          });
          
          // Check if the node itself is a form
          if (node.classList && node.classList.contains('design-upload-form__form') && !node.dataset.uploadInitialized) {
            node.dataset.uploadInitialized = 'true';
            new DesignUploadForm(node);
          }
        }
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

